/**
 * Blog bodies are authored as markdown, but a few posts also contain raw HTML
 * (`<div class="notranslate">`, `<a href>`) and standalone YouTube links. The
 * article renderer does not treat those as markdown, so they leak onto the page.
 * Normalize them here, before render.
 */

const NOTRANSLATE_OPEN = '<div class="notranslate">';

const YOUTUBE_ID = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/;

const THUMB_ID = /\[!\[[^\]]*\]\(https?:\/\/(?:i\.ytimg\.com|img\.youtube\.com)\/vi\/([\w-]{11})\//;

export interface BlogMarkdownParts {
  before: string;
  /** Body that must not be machine-translated (speech transcripts). */
  inside: string | null;
  after: string;
}

export function youtubeIdFromUrl(url: string): string | null {
  return url.match(YOUTUBE_ID)?.[1] ?? null;
}

/** Split a speech post's notranslate wrapper out of the markdown source. */
export function splitNotranslate(markdown: string): BlogMarkdownParts {
  const open = markdown.indexOf(NOTRANSLATE_OPEN);
  const close = markdown.lastIndexOf('</div>');

  if (open === -1 || close === -1 || close < open) {
    return { before: markdown, inside: null, after: '' };
  }

  return {
    before: markdown.slice(0, open),
    inside: markdown.slice(open + NOTRANSLATE_OPEN.length, close),
    after: markdown.slice(close + '</div>'.length),
  };
}

function inlineHtmlAnchors(markdown: string): string {
  return markdown.replace(/<a\s+href="([^"]+)">([^<]*)<\/a>/g, '[$2]($1)');
}

function headingTitle(line: string): string | null {
  const match = line.match(/^#{1,3}\s+(.+)$/);

  if (!match) return null;

  return match[1]
    .replace(/^Video:\s*/i, '')
    .replace(/\*\*/g, '')
    .replace(/[[\]]/g, '')
    .trim();
}

/**
 * Turn a standalone YouTube link into a thumbnail embed, and drop the extra
 * "Click to watch" line when that video already has one.
 */
export function prepareBlogMarkdown(markdown: string): string {
  const withAnchors = inlineHtmlAnchors(markdown);
  const lines = withAnchors.split('\n');
  const thumbs = new Set<string>();

  for (const line of lines) {
    const id = line.match(THUMB_ID)?.[1];

    if (id) thumbs.add(id);
  }

  const out: string[] = [];
  let lastHeading = 'Watch on YouTube';

  for (const line of lines) {
    const heading = headingTitle(line);

    if (heading) lastHeading = heading;

    const onlyLink = line.trim().match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);

    if (onlyLink) {
      const [, label, href] = onlyLink;
      const id = youtubeIdFromUrl(href);

      if (id) {
        const isClickLine = /click to watch/i.test(label);

        if (isClickLine && thumbs.has(id)) continue;

        if (!thumbs.has(id)) {
          const title = (isClickLine ? lastHeading : label.replace(/\s*→\s*$/, '')) || lastHeading;
          thumbs.add(id);
          out.push(
            `[![${title}](https://i.ytimg.com/vi/${id}/hqdefault.jpg)](${href})`
          );
          continue;
        }
      }
    }

    out.push(line);
  }

  return out.join('\n');
}
